import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Search } from "lucide-react";
import { cn } from "../../lib/cn";
import { filterCommandGroups } from "../../lib/command-filter";
import { Dialog } from "../Dialog";
import { SkeletonText } from "../Skeleton";
import { Kbd } from "./Kbd";

export interface CommandItem {
  id: string;
  label: string;
  /** Linha secundária (ex.: "Campanha · arquivada"). */
  description?: string;
  icon?: ReactNode;
  /** Sinônimos que também acham o item ("whatsapp", "número"). */
  keywords?: readonly string[];
  /** Atalho exibido à direita, tecla por tecla (`["g", "d"]`). */
  shortcut?: readonly string[];
  onSelect: () => void;
}

export interface CommandGroup {
  id: string;
  label: string;
  items: readonly CommandItem[];
  /** O grupo ainda está carregando: linhas de esqueleto no lugar dos itens. */
  loading?: boolean;
  /** Não deu para carregar: uma frase, sem esconder os outros grupos. */
  error?: string;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: readonly CommandGroup[];
  placeholder?: string;
  /** Nome acessível do diálogo. */
  title?: string;
  /** Texto do vazio. Padrão: "Nada encontrado para 'xyz'." */
  emptyMessage?: (query: string) => ReactNode;
}

/**
 * Paleta de comandos (I1) — busca + ações num diálogo só, 100% por teclado.
 *
 * Montada sobre o `Dialog` nativo: foco preso, ESC e devolução do foco ao gatilho vêm do
 * navegador. O campo é um `combobox` que controla a `listbox` por `aria-activedescendant`
 * — o foco nunca sai do campo, e o leitor de tela anuncia a opção ativa.
 *
 * Não busca dado nenhum: a tela entrega os grupos prontos (e diz quais ainda carregam).
 */
export function CommandPalette({
  open,
  onOpenChange,
  groups,
  placeholder = "Buscar campanha, grupo ou ação",
  title = "Buscar no painel",
  emptyMessage = (query) => `Nada encontrado para '${query}'.`,
}: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const optionId = (index: number) => `${listId}-opt-${index}`;

  // Reabrir começa do zero: a busca da vez passada não serve para a próxima.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const ready = useMemo(() => groups.filter((g) => !g.loading && !g.error), [groups]);
  const pending = useMemo(() => groups.filter((g) => g.loading || g.error), [groups]);
  const filtered = useMemo(() => filterCommandGroups(ready, query), [ready, query]);
  const flat = useMemo(() => filtered.flatMap((g) => g.items), [filtered]);

  useEffect(() => {
    setActive((current) => (current >= flat.length ? 0 : current));
  }, [flat.length]);

  const choose = (item: CommandItem | undefined) => {
    if (!item) return;
    onOpenChange(false);
    item.onSelect();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (flat.length === 0) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((i) => (i + 1) % flat.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive((i) => (i - 1 + flat.length) % flat.length);
        break;
      case "Home":
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        event.preventDefault();
        setActive(flat.length - 1);
        break;
      case "Enter":
        event.preventDefault();
        choose(flat[active]);
        break;
    }
  };

  useEffect(() => {
    if (!open) return;
    document.getElementById(`${listId}-opt-${active}`)?.scrollIntoView?.({ block: "nearest" });
  }, [active, open, listId]);

  let index = -1;
  const trimmed = query.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} size="lg" className="sm:mt-[12dvh] sm:mb-auto">
      <div className="flex flex-col gap-3 pb-2">
        <div className="flex items-center gap-2 rounded-input border border-border bg-surface px-3 focus-within:ring-2 focus-within:ring-ring">
          <Search aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={flat.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={flat.length > 0 ? optionId(active) : undefined}
            aria-label={placeholder}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            className="h-11 min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-subtle-foreground"
          />
        </div>

        <div id={listId} role="listbox" aria-label="Resultados" className="flex max-h-[50dvh] flex-col gap-3 overflow-y-auto scrollbar-thin">
          {filtered.map((group) => (
            <div key={group.id} role="group" aria-labelledby={`${listId}-${group.id}`}>
              <p id={`${listId}-${group.id}`} className="px-2 pb-1 text-2xs uppercase text-subtle-foreground">
                {group.label}
              </p>
              {group.items.map((item) => {
                index += 1;
                const mine = index;
                const selected = mine === active;
                return (
                  <div
                    key={`${group.id}-${item.id}`}
                    id={optionId(mine)}
                    role="option"
                    aria-selected={selected}
                    onMouseMove={() => setActive(mine)}
                    onClick={() => choose(item)}
                    className={cn(
                      "flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm md:min-h-10",
                      selected ? "bg-primary-soft text-foreground" : "text-foreground",
                    )}
                  >
                    {item.icon ? (
                      <span aria-hidden="true" className="shrink-0 text-muted-foreground [&_svg]:size-4">
                        {item.icon}
                      </span>
                    ) : null}
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate">{item.label}</span>
                      {item.description ? (
                        <span className="truncate text-2xs text-muted-foreground">{item.description}</span>
                      ) : null}
                    </span>
                    {item.shortcut ? (
                      <span className="hidden shrink-0 items-center gap-1 md:inline-flex" aria-hidden="true">
                        {item.shortcut.map((key) => (
                          <Kbd key={key}>{key}</Kbd>
                        ))}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}

          {pending.map((group) => (
            <div key={group.id} aria-busy={group.loading || undefined}>
              <p className="px-2 pb-1 text-2xs uppercase text-subtle-foreground">{group.label}</p>
              {group.loading ? (
                <SkeletonText lines={2} className="px-2" />
              ) : (
                <p className="px-2 text-xs text-muted-foreground">{group.error}</p>
              )}
            </div>
          ))}

          {flat.length === 0 && trimmed.length > 0 && pending.every((g) => !g.loading) ? (
            <p role="status" className="px-2 py-6 text-center text-sm text-muted-foreground">
              {emptyMessage(trimmed)}
            </p>
          ) : null}
        </div>

        <p className="hidden items-center gap-3 px-1 text-2xs text-muted-foreground md:flex" aria-hidden="true">
          <span className="inline-flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> navegar
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>Enter</Kbd> abrir
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>Esc</Kbd> fechar
          </span>
        </p>
      </div>
    </Dialog>
  );
}
